import React, { useMemo } from "react";
import { 
  Languages, 
  Subtitles,
  Eye,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { motion } from "framer-motion";
import { useI18n } from "@/lib/i18n";

interface ImmersiveFloatingActionButtonsProps {
  showSubtitles: boolean;
  setShowSubtitles: (show: boolean) => void;
  showTranslation: boolean;
  setShowTranslation: (show: boolean) => void;
  allTranslationsRevealed: boolean;
  setAllTranslationsRevealed: (show: boolean) => void;
  onExitImmersive: () => void;
}

export const ImmersiveFloatingActionButtons: React.FC<ImmersiveFloatingActionButtonsProps> = ({
  showSubtitles,
  setShowSubtitles,
  showTranslation,
  setShowTranslation,
  allTranslationsRevealed,
  setAllTranslationsRevealed,
  onExitImmersive,
}) => {
  const { t } = useI18n();

  const subtitleLabel = useMemo(
    () =>
      showSubtitles
        ? t('episode.immersiveFloating.subtitles.hide', 'Hide subtitles')
        : t('episode.immersiveFloating.subtitles.show', 'Show subtitles'),
    [showSubtitles, t]
  );

  const translationLabel = useMemo(
    () =>
      showTranslation
        ? t('episode.immersiveFloating.translation.hide', 'Hide card translations')
        : t('episode.immersiveFloating.translation.show', 'Show card translations'),
    [showTranslation, t]
  );

  const revealAllLabel = useMemo(
    () =>
      allTranslationsRevealed
        ? t('episode.immersiveFloating.translationReveal.hide', 'Hide all card translations')
        : t('episode.immersiveFloating.translationReveal.show', 'Reveal all card translations'),
    [allTranslationsRevealed, t]
  );

  const exitLabel = useMemo(
    () => t('episode.immersiveFloating.exit', 'Exit immersive mode'),
    [t]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.25 }}
      className="fixed right-2 sm:right-6 md:right-10 top-[360px] sm:top-[340px] z-50"
    >
      {/* Rail Container */}
      <div className="flex flex-col gap-1.5 p-1.5 rounded-2xl bg-background/70 backdrop-blur-md border border-border/40 shadow-lg">
      {/* Subtitle toggle button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className={`h-9 w-9 rounded-xl border-[1px] transition-all duration-200 ${
                showSubtitles
                  ? "bg-primary/90 text-white hover:bg-primary"
                  : "bg-transparent hover:bg-muted/60"
              }`}
              aria-pressed={showSubtitles}
              aria-label={subtitleLabel}
              onClick={() => setShowSubtitles(!showSubtitles)}
            >
              <Subtitles
                size={16}
                className={showSubtitles ? "text-white" : "text-foreground"}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left" className="px-2 py-1 text-xs">
            <p>{subtitleLabel}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Translation toggle button for vocabulary cards */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className={`h-9 w-9 rounded-xl border-[1px] transition-all duration-200 ${
                showTranslation
                  ? "bg-primary/90 text-white hover:bg-primary"
                  : "bg-transparent hover:bg-muted/60"
              }`}
              aria-pressed={showTranslation}
              aria-label={translationLabel}
              onClick={() => setShowTranslation(!showTranslation)}
            >
              <Languages
                size={16}
                className={showTranslation ? "text-white" : "text-foreground"}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left" className="px-2 py-1 text-xs">
            <p>{translationLabel}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Reveal all translations button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className={`h-9 w-9 rounded-xl border-[1px] transition-all duration-200 ${
                allTranslationsRevealed
                  ? "bg-primary/90 text-white hover:bg-primary"
                  : "bg-transparent hover:bg-muted/60"
              }`}
              aria-pressed={allTranslationsRevealed}
              aria-label={revealAllLabel}
              onClick={() => setAllTranslationsRevealed(!allTranslationsRevealed)}
            >
              <Eye
                size={16}
                className={allTranslationsRevealed ? "text-white" : "text-foreground"}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left" className="px-2 py-1 text-xs">
            <p>{revealAllLabel}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Exit immersive mode button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-xl mt-2 border-[1px] bg-transparent hover:bg-red-500/10 text-red-600 hover:text-red-700 border-red-500/30 transition-all duration-200"
              aria-label={exitLabel}
              onClick={onExitImmersive}
            >
              <X size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left" className="px-2 py-1 text-xs">
            <p>{exitLabel}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      </div>
    </motion.div>
  );
};
